import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../lib/api';
import { Save, Image, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';

const DEFAULT_PROFILE_PIC_SIZES = {
  header: { width: 28, height: 28 },
  profile_page: { width: 96, height: 96 },
  listing_seller: { width: 48, height: 48 },
  message_avatar: { width: 40, height: 40 },
  review_avatar: { width: 32, height: 32 }
};

const PIC_SIZE_LABELS = {
  header: 'Header / Navigation',
  profile_page: 'Profile Page',
  listing_seller: 'Listing Seller Card',
  message_avatar: 'Message Avatar',
  review_avatar: 'Review Avatar'
};

export default function AdminSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [picSizes, setPicSizes] = useState(DEFAULT_PROFILE_PIC_SIZES);

  useEffect(() => {
    adminAPI.getSettings()
      .then(({ data }) => {
        setPicSizes(data.profile_pic_sizes || DEFAULT_PROFILE_PIC_SIZES);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const handleSavePicSizes = async () => {
    setSaving(true);
    try {
      await adminAPI.updateSettings({ profile_pic_sizes: picSizes });
      toast.success('Profile picture sizes updated! Changes will apply on next page load.');
    } catch (err) { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const updatePicSize = (location, dimension, value) => {
    setPicSizes(prev => ({
      ...prev,
      [location]: { ...prev[location], [dimension]: parseInt(value) || 0 }
    }));
  };

  const resetPicSizes = () => {
    setPicSizes(DEFAULT_PROFILE_PIC_SIZES);
    toast.info('Reset to defaults. Click Save to apply.');
  };

  if (loading) return (
    <div className="space-y-4">
      {[...Array(3)].map((_, i) => <div key={i} className="bg-white rounded-lg border border-zinc-200 p-6 animate-pulse"><div className="h-5 bg-zinc-100 rounded w-1/3 mb-4" /><div className="h-10 bg-zinc-100 rounded" /></div>)}
    </div>
  );

  return (
    <div data-testid="admin-settings">
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-zinc-900">Site Settings</h1>
        <p className="text-sm text-zinc-500 mt-1">Manage image sizes and site configuration</p>
      </div>

      {/* Profile Picture Sizes */}
      <div className="bg-white rounded-lg border border-zinc-200 p-6" data-testid="image-settings">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <Image className="w-5 h-5 text-[#002FA7]" />
            <div>
              <h2 className="font-bold text-zinc-900">Profile Picture Sizes</h2>
              <p className="text-sm text-zinc-500">Control the size of profile pictures across different parts of the site (in pixels).</p>
            </div>
          </div>
          <button onClick={resetPicSizes} className="flex items-center gap-1 px-3 py-1.5 rounded-lg border border-zinc-200 text-zinc-600 text-xs font-bold hover:bg-zinc-50 transition-colors" data-testid="reset-pic-sizes-btn">
            <RotateCcw className="w-3 h-3" /> Reset to Defaults
          </button>
        </div>

        <div className="space-y-4">
          {Object.entries(picSizes).map(([location, sizes]) => (
            <div key={location} className="flex flex-col sm:flex-row gap-3 items-start sm:items-center p-4 bg-zinc-50 rounded-lg border border-zinc-200" data-testid={`pic-size-${location}`}>
              <div className="sm:w-48 flex-shrink-0">
                <p className="font-semibold text-sm text-zinc-900">{PIC_SIZE_LABELS[location] || location}</p>
                <p className="text-xs text-zinc-400">Currently {sizes.width}x{sizes.height}px</p>
              </div>
              <div className="flex gap-3 flex-1">
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1 block">Width (px)</label>
                  <input type="number" min="16" max="256" value={sizes.width} onChange={e => updatePicSize(location, 'width', e.target.value)}
                    className="w-24 border border-zinc-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#002FA7]"
                    data-testid={`pic-width-${location}`}
                  />
                </div>
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1 block">Height (px)</label>
                  <input type="number" min="16" max="256" value={sizes.height} onChange={e => updatePicSize(location, 'height', e.target.value)}
                    className="w-24 border border-zinc-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#002FA7]"
                    data-testid={`pic-height-${location}`}
                  />
                </div>
                <div className="flex items-end">
                  <div
                    className="rounded-full bg-[#002FA7] flex items-center justify-center text-white font-bold overflow-hidden flex-shrink-0"
                    style={{ width: `${Math.min(sizes.width, 96)}px`, height: `${Math.min(sizes.height, 96)}px`, fontSize: `${Math.min(sizes.width, 96) * 0.4}px` }}
                  >
                    A
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <button onClick={handleSavePicSizes} disabled={saving}
          className="mt-6 bg-[#002FA7] text-white hover:bg-[#002585] rounded-full px-6 py-2.5 font-bold text-sm disabled:opacity-50 flex items-center gap-2"
          data-testid="save-pic-sizes-btn"
        ><Save className="w-4 h-4" /> {saving ? 'Saving...' : 'Save Picture Sizes'}</button>
      </div>
    </div>
  );
}
